import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AdminFormFieldCheckbox,
  AdminFormFieldCheckboxGroup,
} from "../../../../components/admin/formFields";
import CamImage from "../../../../assets/images/cam_K145C_K245C.png";
import CamImage2 from "../../../../assets/images/cam_K165_K265.png";
import camImage3 from "../../../../assets/images/cam_K220.png";
import UsbAux from "../../../../assets/images/cam_usb_aux.png";
import TviAux from "../../../../assets/images/cam_tvi_aux.png";
import SingleCameraResolution from "../../../../assets/svg/single-camera-resolution.svg";
import SideBySideCameraResolution from "../../../../assets/svg/separate-camera-resolution.svg";
import GridCameraResolution from "../../../../assets/svg/grid-camera-resolution.svg";
import {
  CONVERT_CAMERA_MODEL_NAMES,
  VIDEO_RECORDING_CONSTANTS,
} from "../../constants";
import VideoFormatResolution from "./videoFormatResolution";

const MODELS_IMAGE = {
  K145: CamImage,
  K245: CamImage,
  K165: CamImage2,
  K265: CamImage2,
  K220: camImage3,
};

const NON_EMBEDDED_IMAGES = {
  usb_aux: UsbAux,
  tvi_aux: TviAux,
};

const models = {
  K145: "K145",
  K245: "K245",
  K165: "K165",
  K265: "K265",
  K220: "K220",
};

const Cameras = [
  { label: "Embedded Camera", value: "embedded_camera" },
  { label: "UVC Camera", value: "uvc_camera" },
  { label: "TVi Camera", value: "tvi_camera" },
];

interface VideoRecordingSettingsProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  setFieldValue: (field: string, value: any) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  setFieldTouched: (field: string, isTouched: boolean) => void;
  model: string;
  isSingleConfiguration?: boolean;
}

const VideoRecordingSettings: FC<VideoRecordingSettingsProps> = ({
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  handleBlur,
  setFieldTouched,
  model,
  isSingleConfiguration = false,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.camera_settings",
  });
  const [selectedCamera, setSelectedCamera] = useState<string | null>(
    "embedded_camera"
  );
  return (
    <>
      <div className="col-span-6 p-6 border-b">
        <h1 className="text-lg text-accent-blue-dark font-bold mb-4">
          {t("video_recording")}
        </h1>
        <h2 className="block font-semibold text-sm leading-none tracking-normal mb-4">
          {t("capture_settings")}
        </h2>
        <div className="col-span-6 grid grid-cols-8 gap-6">
          <AdminFormFieldCheckbox
            customWrapperClass="col-span-4"
            label={t("record_sound_in_cab")}
            id="record_sound_in_cab"
            type="checkbox"
            name="record_sound_in_cab"
            checked={values.record_sound_in_cab}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.record_sound_in_cab}
            touched={touched.record_sound_in_cab}
          />
          <AdminFormFieldCheckbox
            customWrapperClass="col-span-4"
            label={t("video_snapshots")}
            id="video_snapshots"
            type="checkbox"
            name="video_snapshots"
            checked={values.video_snapshots}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.video_snapshots}
            touched={touched.video_snapshots}
          />
        </div>
      </div>
      {isSingleConfiguration && (
        <div className="col-span-6 grid grid-cols-12 p-6 space-x-1 space-y-1">
          <h2 className="col-span-12 block font-semibold text-sm leading-none tracking-normal mb-2">
            {t("set_up_camera_by_model")}
          </h2>
          <div className="col-span-4 bg-gray-100 rounded-lg p-2 space-y-2">
            <label className="font-semibold text-sm leading-none tracking-normal text-accent-blue-dark">
              {t("fleet_vision")}
            </label>
            <AdminFormFieldCheckboxGroup
              layout="column"
              options={[
                {
                  label: CONVERT_CAMERA_MODEL_NAMES
                    ? model.replace("K", "FV")
                    : model,
                  value: model,
                },
              ]}
              values={[model]}
              onChange={() => {}}
              selectionMode="single"
            />
          </div>
          <div className="col-span-8 bg-gray-100 rounded-lg p-2 space-y-2">
            <div className="w-full flex font-normal text-base leading-none tracking-normal py-2">
              {Cameras.map((item, index) => {
                const isTviCamera = item.value === "tvi_camera";
                const isEnabledModel =
                  model === models.K165 || model === models.K265;
                const isDisabled = isTviCamera && !isEnabledModel;
                return (
                  <span
                    key={index}
                    onClick={() => {
                      if (!isDisabled) {
                        setSelectedCamera(item.value);
                      }
                    }}
                    className={`px-4 border-r cursor-pointer
                    ${selectedCamera === item.value ? "text-accent-blue-bright" : ""}
                    ${item.value !== "tvi_camera" ? "border-gray-500" : ""}
                    ${isDisabled ? "cursor-not-allowed text-gray-300" : ""}
                  `}
                  >
                    {item.label}
                  </span>
                );
              })}
            </div>
            <div className="w-full flex bg-white rounded-lg justify-center items-center">
              <img
                src={
                  selectedCamera === "embedded_camera"
                    ? MODELS_IMAGE[model as keyof typeof MODELS_IMAGE]
                    : selectedCamera === "uvc_camera"
                      ? NON_EMBEDDED_IMAGES.usb_aux
                      : NON_EMBEDDED_IMAGES.tvi_aux
                }
                alt="Camera"
                className="h-24 self-center"
              />
            </div>
          </div>
          {selectedCamera === "embedded_camera" && (
            <VideoFormatResolution
              selectedCamera={selectedCamera}
              label={t("video")}
              model={model}
              values={values}
              errors={errors}
              touched={touched}
              setFieldTouched={setFieldValue}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              videoResolution={
                values[`vr_${model}_embedded_videoFormat`] === "Separate"
                  ? VIDEO_RECORDING_CONSTANTS.embedded_separate_video_resolution
                  : VIDEO_RECORDING_CONSTANTS.embedded_side_by_side_video_resolution
              }
              cameraResolutionImage={
                values[`vr_${model}_embedded_videoFormat`] === "Separate"
                  ? SingleCameraResolution
                  : SideBySideCameraResolution
              }
            />
          )}
          {selectedCamera === "uvc_camera" && (
            <VideoFormatResolution
              selectedCamera={selectedCamera}
              label={t("video")}
              model={model}
              values={values}
              errors={errors}
              touched={touched}
              setFieldTouched={setFieldValue}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              videoResolution={
                VIDEO_RECORDING_CONSTANTS.uvc_camera_video_resolution
              }
              videoFormat={t("single")}
              cameraResolutionImage={SingleCameraResolution}
            />
          )}
          {selectedCamera === "tvi_camera" && (
            <>
              <VideoFormatResolution
                selectedCamera={selectedCamera}
                label={t("two_camera_sku")}
                model={model}
                values={values}
                errors={errors}
                touched={touched}
                setFieldTouched={setFieldValue}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                videoResolution={
                  VIDEO_RECORDING_CONSTANTS.tvi_two_camera_sku_resolution
                }
                videoFormat={t("side_by_side")}
                cameraResolutionImage={SideBySideCameraResolution}
              />
              <VideoFormatResolution
                selectedCamera={selectedCamera}
                label={t("four_camera_sku")}
                model={model}
                values={values}
                errors={errors}
                touched={touched}
                setFieldTouched={setFieldValue}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                videoResolution={
                  VIDEO_RECORDING_CONSTANTS.tvi_four_camera_sku_resolution
                }
                videoFormat={t("grid")}
                cameraResolutionImage={GridCameraResolution}
              />
            </>
          )}
        </div>
      )}
    </>
  );
};

export default VideoRecordingSettings;
